/**
 * Запросы к probe-точкам в РФ и за рубежом.
 *
 * Это ядро детекта сплит-туннелирования: два запроса с одной страницы на
 * серверы в разных странах. Сравнение того, какой IP увидел каждый сервер,
 * делается на бэкенде (analysis/splitTunnel.ts) — браузеру достаточно оба
 * запроса выполнить и заодно замерить время ответа.
 *
 * Запросы идут параллельно и независимо: недоступность зарубежной точки —
 * штатная ситуация (её могут блокировать), и она не должна ломать проверку.
 */

import type { ProbeResponse } from '@vpncheck/shared';

/** Таймаут на один probe-запрос. Зарубежная точка может не отвечать вовсе. */
const PROBE_TIMEOUT_MS = 8_000;

export interface ProbeOutcome {
  response: ProbeResponse | null;
  /** Время ответа, мс. null — запрос не удался. */
  rttMs: number | null;
  error: string | null;
}

export interface ProbeResults {
  ru: ProbeOutcome;
  intl: ProbeOutcome;
}

/**
 * Обращается к обеим точкам параллельно.
 *
 * Адреса берутся из плана проверок (CheckPlan) как есть: сервер отдаёт их уже
 * вместе с токеном — `https://…/probe/<token>`. Достраивать путь здесь нельзя,
 * иначе получится `/probe/<token>/probe/<token>`.
 */
export async function runProbes(
  ruProbeUrl: string,
  intlProbeUrl: string | null,
): Promise<ProbeResults> {
  const [ru, intl] = await Promise.all([
    callProbe(ruProbeUrl),
    intlProbeUrl
      ? callProbe(intlProbeUrl)
      : Promise.resolve<ProbeOutcome>({
          response: null,
          rttMs: null,
          error: 'зарубежная точка не настроена',
        }),
  ]);
  return { ru, intl };
}

async function callProbe(probeUrl: string): Promise<ProbeOutcome> {
  // Случайный параметр гарантирует, что промежуточный кеш не отдаст старый
  // ответ: probe обязан отработать заново, иначе замер бессмысленен.
  const url = appendCacheBuster(probeUrl);
  const started = performance.now();

  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    const rttMs = performance.now() - started;

    if (!response.ok) {
      return { response: null, rttMs, error: `сервер ответил ${response.status}` };
    }
    const data = (await response.json()) as ProbeResponse;
    return { response: data, rttMs, error: null };
  } catch (err) {
    return {
      response: null,
      rttMs: null,
      error: err instanceof Error ? err.message : 'запрос не выполнен',
    };
  }
}

/**
 * Добавляет анти-кеш параметр, не ломая уже имеющийся query.
 * Относительный URL тоже допустим: база нужна только для разбора.
 */
function appendCacheBuster(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('t', Date.now().toString(36));
    return parsed.toString();
  } catch {
    // Нераспознанный URL отдаём как есть: пусть ошибку покажет fetch.
    return url;
  }
}
